import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";
import {
  ArrowLeft, Edit2, Save, X as XIcon, User as UserIcon, Phone, Briefcase, Shield, GraduationCap,
  Mail, MapPin, Calendar, IdCard, AlertCircle
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

type Emp = any;

const SECTIONS = [
  { key: "personal",  label: "Personal Info",         icon: UserIcon },
  { key: "contact",   label: "Contact",               icon: Phone },
  { key: "employment",label: "Employment",            icon: Briefcase },
  { key: "insurance", label: "Insurance & Gov.",      icon: Shield },
  { key: "skills",    label: "Skills & Education",    icon: GraduationCap },
];

const FIELDS: Record<string, Array<{ key: string; label: string; type?: string; options?: string[]; colspan?: number }>> = {
  personal: [
    { key: "name",           label: "Name (English)",    type: "readonly" },
    { key: "nameAr",         label: "Name (Arabic)" },
    { key: "nationalId",     label: "National ID" },
    { key: "iqamaNumber",    label: "Iqama Number" },
    { key: "iqamaExpiry",    label: "Iqama Expiry",      type: "date" },
    { key: "passportNumber", label: "Passport Number" },
    { key: "passportExpiry", label: "Passport Expiry",   type: "date" },
    { key: "birthDate",      label: "Date of Birth",     type: "date" },
    { key: "nationality",    label: "Nationality" },
    { key: "maritalStatus",  label: "Marital Status",    type: "select", options: ["", "Single", "Married", "Divorced", "Widowed"] },
    { key: "dependents",     label: "Dependents",        type: "number" },
  ],
  contact: [
    { key: "personalPhone",  label: "Primary Phone",     type: "tel" },
    { key: "altPhone",       label: "Alt. Phone",        type: "tel" },
    { key: "personalEmail",  label: "Personal Email",    type: "email" },
    { key: "address",        label: "Address",           type: "textarea", colspan: 2 },
    { key: "emergencyName",  label: "Emergency Name" },
    { key: "emergencyRelation", label: "Relation" },
    { key: "emergencyPhone", label: "Emergency Phone",   type: "tel" },
  ],
  employment: [
    { key: "jobTitle",         label: "Job Title" },
    { key: "department",       label: "Department",       type: "readonly" },
    { key: "hiringDate",       label: "Hiring Date",      type: "date" },
    { key: "probationEndDate", label: "Probation End",    type: "date" },
    { key: "contractType",     label: "Contract Type",    type: "select", options: ["", "Full-time", "Part-time", "Contract", "Temporary"] },
    { key: "employmentStatus", label: "Status",           type: "select", options: ["ACTIVE", "PROBATION", "SUSPENDED", "TERMINATED"] },
    { key: "terminationDate",  label: "Termination Date", type: "date" },
    { key: "terminationReason",label: "Termination Reason", type: "textarea", colspan: 2 },
  ],
  insurance: [
    { key: "gosiNumber",         label: "GOSI Number" },
    { key: "gosiStartDate",      label: "GOSI Start Date",     type: "date" },
    { key: "medicalProvider",    label: "Medical Insurance Co." },
    { key: "medicalPolicyNumber",label: "Policy Number" },
    { key: "medicalClass",       label: "Class",         type: "select", options: ["", "VIP", "A+", "A", "B", "C"] },
    { key: "medicalExpiry",      label: "Medical Expiry",      type: "date" },
  ],
  skills: [
    { key: "highestQualification", label: "Highest Qualification" },
    { key: "specialization",       label: "Specialization" },
    { key: "skills",               label: "Skills",          type: "textarea", colspan: 2 },
    { key: "certifications",       label: "Certifications",  type: "textarea", colspan: 2 },
  ],
};

function fmtDateForInput(iso: any): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function fmtDateDisplay(iso: any): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function daysUntil(iso: any): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / (1000 * 60 * 60 * 24));
}

function ExpiryHint({ iso, label }: { iso: any; label: string }) {
  const days = daysUntil(iso);
  if (days === null) return null;
  const color = days < 0 ? "text-red-700 bg-red-50" : days <= 30 ? "text-orange-700 bg-orange-50" : days <= 90 ? "text-amber-700 bg-amber-50" : "text-green-700 bg-green-50";
  return (
    <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${color}`}>
      {days < 0 ? <><AlertCircle size={10}/> Expired</> : `${days}d left`}
    </span>
  );
}

export default function EmployeeProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [emp, setEmp] = useState<Emp | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>({});
  const [activeSection, setActiveSection] = useState("personal");

  const canEdit = user?.role === "ADMIN" || user?.role === "HR";
  const isSelf = user?.id === id;

  const load = () => {
    if (!id) return;
    setLoading(true);
    api.get(`/hr/employees/${id}`)
      .then(r => { setEmp(r.data); setForm(r.data); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const startEdit = () => {
    setForm({ ...emp });
    setEditing(true);
  };

  const cancelEdit = () => {
    setForm({ ...emp });
    setEditing(false);
  };

  const save = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const r = await api.put(`/hr/employees/${id}`, form);
      setEmp(r.data);
      setForm(r.data);
      setEditing(false);
      toast.success("Saved");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const setField = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;
  if (!emp) return <div className="p-6 text-gray-400">Employee not found.</div>;

  return (
    <div className="max-w-5xl mx-auto p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700 p-1">
          <ArrowLeft size={22}/>
        </button>
        <div className="w-16 h-16 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-2xl font-bold shrink-0">
          {emp.name?.[0] || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold text-gray-900 truncate">
            {emp.name} {emp.nameAr && <span className="text-sm text-gray-500 ml-2">· {emp.nameAr}</span>}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            {emp.jobTitle && <span>{emp.jobTitle}</span>}
            {emp.department && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">{emp.department}</span>}
            <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">{emp.role}</span>
            {emp.employmentStatus && emp.employmentStatus !== "ACTIVE" && (
              <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">{emp.employmentStatus}</span>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {!editing ? (
              <button onClick={startEdit} className="flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 text-sm">
                <Edit2 size={14}/> Edit
              </button>
            ) : (
              <>
                <button onClick={cancelEdit} className="flex items-center gap-1.5 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm">
                  <XIcon size={14}/> Cancel
                </button>
                <button onClick={save} disabled={saving} className="flex items-center gap-1.5 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm disabled:opacity-50">
                  <Save size={14}/> {saving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200 overflow-x-auto">
        {SECTIONS.map(s => {
          const Icon = s.icon;
          const active = activeSection === s.key;
          return (
            <button key={s.key} onClick={() => setActiveSection(s.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition ${
                active ? "border-indigo-600 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}>
              <Icon size={14}/> {s.label}
            </button>
          );
        })}
      </div>

      {/* Section fields */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS[activeSection].map(f => {
            const val = form?.[f.key];
            const isReadonly = f.type === "readonly" || !editing;
            const expiryHint = ["iqamaExpiry","passportExpiry","medicalExpiry"].includes(f.key);
            return (
              <div key={f.key} className={f.colspan === 2 ? "sm:col-span-2" : ""}>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {f.label}
                  {expiryHint && <ExpiryHint iso={val} label={f.label}/>}
                </label>
                {isReadonly ? (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-100 rounded-lg text-sm text-gray-800 min-h-[38px]">
                    {f.type === "date" ? fmtDateDisplay(val) : (val ?? <span className="text-gray-300">—</span>)}
                  </div>
                ) : f.type === "textarea" ? (
                  <textarea value={val || ""} onChange={e => setField(f.key, e.target.value)} rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
                ) : f.type === "select" ? (
                  <select value={val || ""} onChange={e => setField(f.key, e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    {f.options?.map(o => <option key={o} value={o}>{o || "—"}</option>)}
                  </select>
                ) : f.type === "date" ? (
                  <input type="date" value={fmtDateForInput(val)} onChange={e => setField(f.key, e.target.value || null)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
                ) : f.type === "number" ? (
                  <input type="number" min="0" value={val ?? ""} onChange={e => setField(f.key, e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
                ) : (
                  <input type={f.type || "text"} value={val || ""} onChange={e => setField(f.key, e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
